import type { AppRouteContext } from "@/types/route"
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
} from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { createServerFn } from "@tanstack/react-start"
import { getCookie, getRequest, setCookie } from "@tanstack/react-start/server"
import * as React from "react"
import BlueskyIcon from "@/components/icons/BlueskyIcon"
import GitHubIcon from "@/components/icons/GitHubIcon"
import appCss from "@/styles/main.css?url"
import { useRouteReload } from "@/utils/reload"
import "@/types/css"

const getServerTheme = createServerFn({ method: "GET" }).handler(
  async () => {
    const theme = getCookie("theme")
    if (theme === "light" || theme === "dark") {
      return theme
    }
    return undefined
  },
)

const setServerTheme = createServerFn({ method: "POST" })
  .inputValidator((theme: string) => theme)
  .handler(
    ({ data: theme }) => {
      setCookie("theme", theme, {
        path: "/",
        secure: false,
        sameSite: "lax",
        maxAge: 60 * 60 * 24 * 365,
        httpOnly: true,
      })
    },
  )

export const Route = createRootRouteWithContext<AppRouteContext>()({
  loader: async () => {
    const theme = await getServerTheme()
    return {
      theme,
    }
  },
  component: RootComponent,
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // preload fonts (optional, but recommended for performance)
      { rel: "preload", href: "https://fonts.gstatic.com/s/manrope/v19/xn7gYHE41ni1AdIRggexSg.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: "https://fonts.gstatic.com/s/sourceserif4/v13/vEFI2_tTDB4M7-auWDN0ahZJW1gb8tc.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400&family=Manrope:wght@400;500;600&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap" },
      { rel: "stylesheet", href: appCss },
      { rel: "alternate", type: "application/atom+xml", title: "RSS", href: "/feeds/default.xml" },
    ],
    styles: [
      // preload fonts (optional, but recommended for performance)
      {
        children: `
          @font-face {
            font-family: 'Source Serif 4';
            font-style: normal;
            font-weight: 600;
            font-display: swap;
            src: url(https://fonts.gstatic.com/s/sourceserif4/v13/vEFI2_tTDB4M7-auWDN0ahZJW1gb8tc.woff2) format('woff2');
            unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
          }

          @font-face {
            font-family: 'Manrope';
            font-style: normal;
            font-weight: 400;
            font-display: swap;
            src: url(https://fonts.gstatic.com/s/manrope/v19/xn7gYHE41ni1AdIRggexSg.woff2) format('woff2');
            unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
          }
        `,
        type: "text/css",
      },
    ],
  }),
})

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const { theme } = Route.useLoaderData()

  useRouteReload()

  const toggleTheme = () => {
    const currentTheme = document.documentElement.getAttribute("data-theme")
      || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    const newTheme = (currentTheme === "dark") ? "light" : "dark"
    document.documentElement.setAttribute("data-theme", newTheme)
    setServerTheme({ data: newTheme })
  }

  return (
    <html lang="en" data-theme={theme}>
      <head>
        <HeadContent />
      </head>
      <body className="site">
        <header className="root-header">
          <a href="/" aria-label="Go to home page of jingsi.space">
            <span className="hidden">
              jingsi.space
            </span>
            <svg
              role="img"
              viewBox="0 0 440 440"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              style={{ color: "#c583c0", borderRadius: "100%", height: "var(--logo-height)" }}
            >
              <rect width="440" height="440" fill="#e5e5e5" />
              <path
                d="M 35 35 L 385 35 L 385 175 L 315 175 L 315 245 L 245 245 L 245 315 L 175 315 L 175 245 L 105 245 L 105 175 L 35 175 Z"
                fill="currentColor"
              />
            </svg>
          </a>
        </header>

        <nav className="root-nav">
          <ul aria-label="Main navigation menu">
            <li>
              <Link to="/posts">
                Archive
              </Link>
            </li>
            <li>
              <Link to="/about">
                About
              </Link>
            </li>
            <li>
              <a href="/feeds/default.xml" aria-label="RSS feed">
                <svg width="16" height="16" viewBox="-3 0 17 17"><path d="M.926 12.818a1.403 1.403 0 1 1 0 1.984 1.402 1.402 0 0 1 0-1.984zm10.531 2.357a1.03 1.03 0 0 1-1.029-1.03 8.775 8.775 0 0 0-.694-3.438A8.826 8.826 0 0 0 1.591 5.31a1.03 1.03 0 1 1 0-2.059 10.817 10.817 0 0 1 4.24.857 10.893 10.893 0 0 1 3.463 2.334 10.867 10.867 0 0 1 3.19 7.703 1.027 1.027 0 0 1-1.027 1.029zm-4.538 0a1.03 1.03 0 0 1-1.029-1.03 4.297 4.297 0 0 0-4.299-4.298 1.03 1.03 0 0 1 0-2.059 6.362 6.362 0 0 1 5.857 3.883 6.298 6.298 0 0 1 .5 2.475 1.03 1.03 0 0 1-1.029 1.029z" fill="currentColor" /></svg>
                <span className="hidden">
                  RSS
                </span>
              </a>
            </li>
            <li>
              <a href="https://github.com/akirak" aria-label="GitHub profile">
                <GitHubIcon width="16" height="16" />
                <span className="hidden">
                  GitHub
                </span>
              </a>
            </li>
            <li>
              <a href="https://bsky.app/profile/akirakom.bsky.social" aria-label="Bluesky profile">
                <BlueskyIcon width="16" height="16" />
                <span className="hidden">
                  Bluesky
                </span>
              </a>
            </li>
            <li>
              <button type="button" onClick={toggleTheme} aria-label="Toggle theme">
                <span className="hidden">
                  Toggle theme
                </span>
              </button>
            </li>
          </ul>
        </nav>

        {children}

        <footer className="root-footer">
          <div aria-label="Copyright of this site">
            © 2025-2026
            {" "}
            <Link to="/about">Akira Komamura</Link>
          </div>

          <div aria-label="License of this site">
            <a
              href="https://creativecommons.org/licenses/by-nc-sa/4.0/"
              aria-label="Read the license of this site content"
            >
              CC BY-NC-SA 4.0
            </a>
          </div>

          <div>
            <a
              href="https://github.com/akirak/late-stack"
              aria-label="Source code of this site"
              target="_blank"
              rel="noopener noreferrer"
            >
              Built with TypeScript
            </a>
          </div>
        </footer>

        <TanStackRouterDevtools position="bottom-right" />
        <Scripts />
      </body>
    </html>
  )
}
